import React, {useEffect, useRef, useState} from "react";
import {Dropdown} from "react-bootstrap";
import {Bell, BellOff, Check, CheckCheck, CircleAlert, CircleCheck, CircleX, Copy, Trash2, TriangleAlert} from "lucide-react";

import Tip from "@components/Tip/Tip";
import {NOTIFICATION_TYPES, useNotifications, useNotificationsState} from "@context/NotificationContext.jsx";
import "./NotificationBell.css";

const TYPE_ICONS = {
    success: CircleCheck,
    warning: TriangleAlert,
    info: CircleAlert,
    error: CircleX,
};

const timeAgo = (timestamp) => {
    const minutes = Math.floor((Date.now() - timestamp) / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (minutes < 60 * 24) return `${Math.floor(minutes / 60)}h ago`;
    return `${Math.floor(minutes / (60 * 24))}d ago`;
};

// TopNav bell: a badge of unread notifications and a dropdown of the stored history. Hovering a row marks it read.
// Clicking a toast opens the dropdown scrolled to that notification (panel.focusId).
export default function NotificationBell() {
    const {notifications, panel} = useNotificationsState();
    const {addNotification, markRead, markAllRead, clearNotifications, setPanelOpen} = useNotifications();
    const [copiedId, setCopiedId] = useState(null);
    const focusedRow = useRef(null);

    useEffect(() => {
        if (panel.open) focusedRow.current?.scrollIntoView({block: "nearest"});
    }, [panel.open, panel.focusId]);

    const unread = notifications.filter((n) => !n.read).length;

    const copy = async (notification) => {
        try {
            await navigator.clipboard.writeText(notification.message);
            setCopiedId(notification.id);
            setTimeout(() => setCopiedId((id) => id === notification.id ? null : id), 1500);
        } catch (error) {
            addNotification(`Error copying to clipboard: ${error}`, NOTIFICATION_TYPES.ERROR);
        }
    };

    return (
        <Dropdown align="end" autoClose="outside" className="notif-bell" show={panel.open} onToggle={setPanelOpen}>
            <Dropdown.Toggle as="button" type="button" className="notif-bell-toggle" aria-label="Notifications">
                <Tip
                    placement="bottom"
                    tooltipId="notif-bell-tooltip"
                    text={unread ? `${unread} unread notification${unread === 1 ? "" : "s"}` : "No new notifications"}
                >
                    <span className="notif-bell-icon">
                        <Bell size={20}/>
                        {unread > 0 && <span className="notif-bell-badge">{unread > 9 ? "9+" : unread}</span>}
                    </span>
                </Tip>
            </Dropdown.Toggle>

            <Dropdown.Menu className="notif-panel">
                <div className="notif-panel-head">
                    <span className="notif-panel-title">
                        Notifications
                        {unread > 0 && <span className="notif-panel-unread">{unread} unread</span>}
                    </span>
                    <button type="button" className="notif-link" onClick={markAllRead} disabled={!unread}>
                        <CheckCheck size={16}/> Mark all as read
                    </button>
                </div>

                {notifications.length === 0 ? (
                    <div className="notif-empty">
                        <BellOff size={28}/>
                        You're all caught up
                    </div>
                ) : (
                    <div className="notif-list">
                        {notifications.map((n) => {
                            const Icon = TYPE_ICONS[n.type] ?? CircleAlert;
                            return (
                                <div
                                    key={n.id}
                                    ref={n.id === panel.focusId ? focusedRow : null}
                                    className={`notif-row notif-${n.type} ${n.read ? "" : "unread"} ${
                                        n.id === panel.focusId ? "focused" : ""}`}
                                    onMouseEnter={() => !n.read && markRead(n.id)}
                                >
                                    <Icon className="notif-type-icon" size={18}/>
                                    <div className="notif-main">
                                        <div className="notif-msg">{n.message}</div>
                                        <div className="notif-meta">
                                            {timeAgo(n.timestamp)}
                                            {n.count > 1 && <span className="notif-count">×{n.count}</span>}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="notif-copy"
                                        aria-label="Copy message"
                                        onClick={() => copy(n)}
                                    >
                                        {copiedId === n.id ? <Check size={15}/> : <Copy size={15}/>}
                                    </button>
                                    {!n.read && <span className="notif-dot"/>}
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="notif-panel-foot">
                    <button
                        type="button"
                        className="notif-link muted"
                        onClick={clearNotifications}
                        disabled={!notifications.length}
                    >
                        <Trash2 size={15}/> Clear all
                    </button>
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
}
